import { useTranslation } from "react-i18next";
import { FormContainer, SwitchInput, Treturn, useFetch } from "@4uhub/lib4uhub";
import { memo, useCallback } from "react";
import { Grid } from "@mui/material";
import { preferencesSchema, TPreferencesForm } from "./preferencesSchema";
import {
  createTicketAutomationReceivedRulePreference,
  getTicketAutomationReceivedRulePreferenceById,
  updateTicketAutomationReceivedRulePreferenceById,
} from "../../../../../../../services/ticketAutomationReceivedRulePreference.service";
import { IPreferencesProps } from "./models";
import { MedicalSpecialty } from "./MedicalSpecialty";
import { CalendarPreferences } from "./CalendarPreferences";

const translationPath =
  "chatBot.page.automations.automationReceivedRules.ticketAutomationReceivedRulePreference.";

export const Preferences = memo(
  ({ id, ticketAutomationReceivedRuleId, onBackButton }: IPreferencesProps) => {
    const { t } = useTranslation();

    const { sendRequest: save, loading: sLoading } = useFetch(
      createTicketAutomationReceivedRulePreference,
    );

    const { sendRequest: update, loading: uLoading } = useFetch(
      updateTicketAutomationReceivedRulePreferenceById,
    );

    const { sendRequest: get, loading: gLoading } = useFetch(
      getTicketAutomationReceivedRulePreferenceById,
    );

    const updateHandler = (v: TPreferencesForm, id: string) =>
      update({
        item: {
          id: id,
          ...v,
          ticketAutomationReceivedRuleId: ticketAutomationReceivedRuleId ?? "",
          medicalSpecialtyExternalId: v.medicalSpecialty.id,
          medicalSpecialtyExternalName: v.medicalSpecialty.name,
          ticketAutomationRecRulePrefPrefs: v.calendarPreferences?.map(
            (calendarPreference) => ({
              externalId: calendarPreference.id,
              externalName: calendarPreference.name,
            }),
          ),
        },
        id: id,
      });

    const saveHandler = (v: TPreferencesForm) =>
      save({
        ...v,
        ticketAutomationReceivedRuleId: ticketAutomationReceivedRuleId ?? "",
        medicalSpecialtyExternalId: v.medicalSpecialty.id,
        medicalSpecialtyExternalName: v.medicalSpecialty.name,
        ticketAutomationRecRulePrefPrefs: v.calendarPreferences?.map(
          (calendarPreference) => ({
            externalId: calendarPreference.id,
            externalName: calendarPreference.name,
          }),
        ),
      });

    const getHandler = useCallback(
      async (id: string): Promise<Treturn<TPreferencesForm>> => {
        const { data, success } = await get(id);

        if (data) {
          const newData: TPreferencesForm = {
            ...data,
            medicalSpecialty: {
              id: data.medicalSpecialtyExternalId,
              name: data.medicalSpecialtyExternalName,
            },
            calendarPreferences: data.ticketAutomationRecRulePrefPrefs?.map(
              (calendarPreference) => ({
                id: calendarPreference.externalId,
                name: calendarPreference.externalName,
              }),
            ),
          };
          return { data: newData, success: true };
        }
        return { data, success };
      },
      [get],
    );

    const loading = sLoading || uLoading || gLoading;

    return (
      <FormContainer<TPreferencesForm>
        saveFunction={saveHandler}
        updateFunction={updateHandler}
        getFunction={getHandler}
        loading={loading}
        title={t(translationPath + "preference")}
        schema={preferencesSchema}
        subtitle={t(translationPath + "new_preference")}
        backButtonOnClick={onBackButton}
        redirectOnSave={false}
        onSuccess={onBackButton}
        isModalForm={true}
        propId={id}
        formActionsOnBottom={false}
        fields={() => (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <SwitchInput
                name="enable"
                label={t(translationPath + "enable")}
              />
            </Grid>

            <MedicalSpecialty />

            <CalendarPreferences />
          </Grid>
        )}
      />
    );
  },
);
